import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Edit2, X, Check, RefreshCw, Settings2, Calendar, Clock, Wrench, XCircle } from 'lucide-react';
import getApiUrl from '../../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


const CATEGORIES = [
  { id: 'rdv', label: 'Prendre RDV', icon: Calendar, color: 'emerald' },
  { id: 'interesses', label: 'Interesse produits', icon: Wrench, color: 'violet' },
  { id: 'bloques', label: 'Bloque / Rappeler', icon: Clock, color: 'amber' },
  { id: 'negatifs', label: 'Negatif / Perdu', icon: XCircle, color: 'red' },
];

const COLOR_MAP = {
  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', badge: 'bg-emerald-500/20' },
  violet: { bg: 'bg-violet-500/10', border: 'border-violet-500/30', text: 'text-violet-400', badge: 'bg-violet-500/20' },
  amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', badge: 'bg-amber-500/20' },
  red: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', badge: 'bg-red-500/20' },
};

export default function StatutsQualificationConfigPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('rdv');
  const [editingCode, setEditingCode] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newStatut, setNewStatut] = useState({ code: '', label: '', desc: '', description: '', trigger: '', delai: '', actif: true });
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');

  const getHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json',
  });

  const loadTenants = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/billing/admin/overview`, { headers: getHeaders() });
      if (r.ok) {
        const d = await r.json();
        const list = (d.tenants || []).filter(t => t.tenant_id);
        setTenants(list);
        if (list.length > 0 && !selectedTenant) setSelectedTenant(list[0].tenant_id);
      }
    } catch (e) { console.error(e); }
  }, []);

  const loadData = useCallback(async () => {
    if (!selectedTenant) return;
    setLoading(true);
    try {
      const r = await authFetch(`${API}/config/pipeline/statuts-qualification?tenant_id=${selectedTenant}`, { headers: getHeaders() });
      if (r.ok) {
        const d = await r.json();
        setData(d.data);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [selectedTenant]);

  useEffect(() => { loadTenants(); }, [loadTenants]);
  useEffect(() => { if (selectedTenant) loadData(); }, [selectedTenant, loadData]);

  const handleAdd = async () => {
    if (!newStatut.code.trim() || !newStatut.label.trim()) return;
    setSaving(true);
    try {
      const r = await authFetch(`${API}/config/pipeline/statuts-qualification/${activeTab}/add?tenant_id=${selectedTenant}`, {
        method: 'POST', headers: getHeaders(), body: JSON.stringify(newStatut),
      });
      if (r.ok) {
        setShowAddForm(false);
        setNewStatut({ code: '', label: '', desc: '', description: '', trigger: '', delai: '', actif: true });
        await loadData();
      } else {
        const err = await r.json();
        alert(err.detail || 'Erreur');
      }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Supprimer le statut "${code}" ?`)) return;
    try {
      const r = await authFetch(`${API}/config/pipeline/statuts-qualification/${activeTab}/${encodeURIComponent(code)}?tenant_id=${selectedTenant}`, {
        method: 'DELETE', headers: getHeaders(),
      });
      if (r.ok) await loadData();
    } catch (e) { console.error(e); }
  };

  const handleEdit = (statut) => {
    setEditingCode(statut.code);
    setEditForm({ ...statut });
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const r = await authFetch(`${API}/config/pipeline/statuts-qualification/${activeTab}/${encodeURIComponent(editingCode)}?tenant_id=${selectedTenant}`, {
        method: 'PUT', headers: getHeaders(), body: JSON.stringify(editForm),
      });
      if (r.ok) { setEditingCode(null); await loadData(); }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const toggleActif = async (statut) => {
    const catList = data?.[activeTab] || [];
    const updated = catList.map(s => s.code === statut.code ? { ...s, actif: !s.actif } : s);
    const newData = { ...data, [activeTab]: updated };
    setData(newData);
    try {
      await authFetch(`${API}/config/pipeline/statuts-qualification?tenant_id=${selectedTenant}`, {
        method: 'POST', headers: getHeaders(), body: JSON.stringify({ data: newData }),
      });
    } catch (e) { console.error(e); }
  };

  const catItems = data?.[activeTab] || [];
  const catInfo = CATEGORIES.find(c => c.id === activeTab);
  const colors = COLOR_MAP[catInfo?.color || 'emerald'];
  const tenantName = tenants.find(t => t.tenant_id === selectedTenant)?.name || selectedTenant;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/30 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center">
            <Settings2 className="w-5 h-5 text-indigo-400" />
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-white" data-testid="statuts-qualification-title">Statuts de Qualification</h2>
            <p className="text-sm text-slate-400">Gerez les statuts du workflow Nouveau Lead par societe</p>
          </div>
          <button onClick={loadData} className="p-2 rounded-lg bg-slate-700/50 hover:bg-slate-700 transition-colors" data-testid="refresh-statuts-btn">
            <RefreshCw className={`w-4 h-4 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Tenant selector */}
      <div className="flex items-center gap-3">
        <label className="text-sm text-slate-400 font-medium whitespace-nowrap">Societe :</label>
        <select value={selectedTenant} onChange={e => setSelectedTenant(e.target.value)}
          data-testid="tenant-selector-statuts"
          className="flex-1 px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:border-indigo-500 focus:outline-none">
          {tenants.map(t => <option key={t.tenant_id} value={t.tenant_id}>{t.name}</option>)}
        </select>
      </div>

      {/* Category tabs */}
      <div className="flex gap-1 bg-slate-800/50 rounded-xl p-1">
        {CATEGORIES.map(cat => {
          const c = COLOR_MAP[cat.color];
          const Icon = cat.icon;
          const count = (data?.[cat.id] || []).length;
          return (
            <button key={cat.id} onClick={() => { setActiveTab(cat.id); setShowAddForm(false); setEditingCode(null); }}
              data-testid={`tab-${cat.id}`}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                activeTab === cat.id ? `${c.bg} ${c.text} ${c.border} border` : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}>
              <Icon className="w-3.5 h-3.5" />
              {cat.label}
              <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] ${activeTab === cat.id ? c.badge : 'bg-slate-700'}`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Statut list */}
      <div className={`bg-slate-800/50 border rounded-xl overflow-hidden ${colors.border}`}>
        <div className="flex items-center justify-between p-3 border-b border-slate-700">
          <span className="text-sm font-medium text-slate-300">{catItems.length} statut(s) — {tenantName}</span>
          <button onClick={() => setShowAddForm(true)} data-testid="add-statut-btn"
            className={`flex items-center gap-1.5 px-3 py-1.5 ${colors.badge} ${colors.text} rounded-lg text-xs font-medium hover:opacity-80 transition-opacity`}>
            <Plus className="w-3.5 h-3.5" /> Ajouter
          </button>
        </div>

        {/* Add form */}
        {showAddForm && (
          <div className="p-3 border-b border-slate-700 bg-slate-900/50 space-y-2">
            <div className="grid grid-cols-3 gap-2">
              <input placeholder="Code (ex: RDV_CUSTOM)" value={newStatut.code} onChange={e => setNewStatut({ ...newStatut, code: e.target.value })}
                data-testid="new-statut-code" className="px-3 py-2 bg-slate-800 border border-slate-600 rounded text-white text-sm focus:border-indigo-500 focus:outline-none" />
              <input placeholder="Label affiche" value={newStatut.label} onChange={e => setNewStatut({ ...newStatut, label: e.target.value })}
                data-testid="new-statut-label" className="px-3 py-2 bg-slate-800 border border-slate-600 rounded text-white text-sm focus:border-indigo-500 focus:outline-none" />
              <input placeholder="Description" value={newStatut.desc || newStatut.description} onChange={e => setNewStatut({ ...newStatut, desc: e.target.value, description: e.target.value })}
                className="px-3 py-2 bg-slate-800 border border-slate-600 rounded text-white text-sm focus:border-indigo-500 focus:outline-none" />
            </div>
            <div className="flex gap-2">
              <button onClick={handleAdd} disabled={saving || !newStatut.code.trim() || !newStatut.label.trim()}
                data-testid="confirm-add-statut-btn"
                className={`flex items-center gap-1 px-4 py-2 ${colors.badge} ${colors.text} rounded text-xs font-medium hover:opacity-80 disabled:opacity-50 transition-opacity`}>
                <Check className="w-3.5 h-3.5" /> Ajouter
              </button>
              <button onClick={() => setShowAddForm(false)} className="px-3 py-2 bg-slate-700 text-slate-400 rounded text-xs hover:bg-slate-600 transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        {/* Items */}
        {loading ? (
          <div className="p-6 text-center text-slate-500">Chargement...</div>
        ) : !data ? (
          <div className="p-6 text-center text-slate-500">Aucune configuration pour ce tenant (valeurs par defaut utilisees)</div>
        ) : catItems.length === 0 ? (
          <div className="p-6 text-center text-slate-500">Aucun statut dans cette categorie</div>
        ) : (
          <div className="divide-y divide-slate-700/50">
            {catItems.map((statut) => (
              <div key={statut.code} className={`flex items-center gap-3 p-3 hover:bg-slate-700/20 transition-colors ${!statut.actif ? 'opacity-50' : ''}`}>
                {editingCode === statut.code ? (
                  <>
                    <input value={editForm.code} onChange={e => setEditForm({ ...editForm, code: e.target.value })}
                      className="w-32 px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm" />
                    <input value={editForm.label} onChange={e => setEditForm({ ...editForm, label: e.target.value })}
                      className="flex-1 px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm" />
                    <input value={editForm.desc || editForm.description || ''} onChange={e => setEditForm({ ...editForm, desc: e.target.value, description: e.target.value })}
                      className="flex-1 px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm" placeholder="Description" />
                    <button onClick={handleSaveEdit} disabled={saving}
                      className="p-1.5 rounded bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 transition-colors">
                      <Check className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setEditingCode(null)}
                      className="p-1.5 rounded bg-slate-700 text-slate-400 hover:bg-slate-600 transition-colors">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white font-medium truncate">{statut.label}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {statut.code}
                        {statut.desc && ` — ${statut.desc}`}
                        {statut.description && !statut.desc && ` — ${statut.description}`}
                        {statut.delai && ` (${statut.delai})`}
                      </div>
                    </div>
                    <button onClick={() => toggleActif(statut)}
                      className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                        statut.actif ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                      }`}>
                      {statut.actif ? 'Actif' : 'Inactif'}
                    </button>
                    <button onClick={() => handleEdit(statut)}
                      className="p-1.5 rounded bg-slate-700/50 text-slate-400 hover:bg-slate-700 hover:text-white transition-colors">
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleDelete(statut.code)}
                      className="p-1.5 rounded bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
